import {
  IProductCategoryShowOrder,
  NotificationType,
} from "@evennt-tech/api-v2-core";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { apiV2 } from "api/firebase";
import { useShowProductsStore } from "store/product/show-products.store";
import { useShallow } from "zustand/react/shallow";

type State = {
  items: IProductCategoryShowOrder[] | null;
  isLoading: boolean;
  loadedShowId: string | null;
};

type Action = {
  fetch: (showId: string, force?: boolean) => Promise<void>;
  autoCatch: (showId: string) => () => void;
};

type Store = State & Action;

const LISTENER_ID = "product_category_show_order_store";

export const productCategoryShowStore = create(
  devtools<Store>(
    (set, get) => ({
      items: null,
      isLoading: false,
      loadedShowId: null,
      fetch: async (showId, force = false) => {
        try {
          const { isLoading, items, loadedShowId } = get();
          if (!force) {
            if (isLoading || (items && loadedShowId === showId)) return;
          }
          set({ isLoading: true });

          const data = await apiV2.productCategoryShowOrder.getOne({ showId });
          set({ items: data, loadedShowId: showId });
        } catch (e) {
          console.error("Error occurred fetching product category order: ", e);
        } finally {
          set({ isLoading: false });
        }
      },
      autoCatch: (showId) => {
        apiV2.notification.subscribe({
          key: NotificationType.UPDATED_PRODUCT_CATEGORY_ORDER_ON_SHOW,
          showId,
          listenerId: LISTENER_ID,
          callback: async () => {
            if (get().loadedShowId !== showId) return;
            await get().fetch(showId, true);

            const {
              groupedProductsParams,
              fetchManyByShowGroupedByCategory,
            } = useShowProductsStore.getState();

            if (groupedProductsParams?.showId !== showId) return;

            await fetchManyByShowGroupedByCategory(groupedProductsParams, {
              preserveLoadedPages: true,
            });
          },
        });

        return () => {
          apiV2.notification.unsubscribe({
            key: NotificationType.UPDATED_PRODUCT_CATEGORY_ORDER_ON_SHOW,
            showId,
            listenerId: LISTENER_ID,
          });
        };
      },
    }),
    { name: "product-category-show-order" }
  )
);

export const useProductCategoryShowOrderStoreActions = () =>
  productCategoryShowStore(
    (state) =>
      ({
        fetchProductCategoryShowOrder: state.fetch,
        autoCatchProductCategoryShowOrder: state.autoCatch,
      } as const)
  );
export const useProductCategoryOrderOnShow = () =>
  productCategoryShowStore(
    useShallow((state) => [state.items, state.isLoading] as const)
  );
